import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    TextField,
    MenuItem,
    Button,
    Slide,
    Typography,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import { DEPARTMENT_OPTIONS, EMPLOYMENT_TYPE_OPTIONS } from './employeeConstants';
import { useSocietyRoles, roleToOption } from './useSocietyRoles';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import {
    MARITAL_STATUS_OPTIONS,
    BLOOD_GROUP_OPTIONS,
    BANK_ACCOUNT_TYPE_OPTIONS,
    CONFIRM_ACCOUNT_KEY,
    emptyProfile,
    profileFromEmployee,
    normalizeProfileInput,
    validateProfile,
    buildProfilePayload,
} from '../../../../consts/employeeProfileFields';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const emptyForm = {
    society_id: '',
    first_name: '',
    last_name: '',
    phone: '',
    employee_type_id: '',
    role_id: '',
    email: '',
    password: '',
    designation: '',
    department: '',
    employment_type: '',
    joining_date: '',
    ...emptyProfile,
};

const toDateInput = (v) => {
    if (!v) return '';
    // Accept ISO timestamps or plain dates → YYYY-MM-DD for <input type=date>
    const str = String(v);
    return str.length >= 10 ? str.slice(0, 10) : str;
};

// Shared field styling for a consistent, professional look.
const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' },
    '& .MuiInputBase-input': { fontSize: 13 },
    '& .MuiInputLabel-root': { fontSize: 13 },
    '& .MuiFormHelperText-root': { fontSize: 11, marginLeft: 0 },
};

const Section = ({ icon, title, children }) => (
    <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.75 }}>
            <Icon icon={icon} style={{ fontSize: 15, color: '#94a3b8' }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748b', whiteSpace: 'nowrap' }}>
                {title}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#e5e9f0' }} />
        </Box>
        {children}
    </Box>
);

// Responsive 2-column field grid. `minmax(0, 1fr)` stops selects/inputs from
// forcing a column wider than its share (which made the dropdowns collapse).
const FieldGrid = ({ children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        {children}
    </Box>
);

/**
 * Shared Add / Edit employee modal.
 * Parent owns the dispatch — this collects a clean payload and calls onSubmit(payload).
 */
const EmployeeFormModal = ({
    open,
    mode = 'add',
    employee,
    societyOptions = [],
    typeOptions = [],
    submitting = false,
    onClose,
    onSubmit,
}) => {
    const isEdit = mode === 'edit';
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});

    // RBAC roles for the selected community's role picker.
    const { roles, loading: rolesLoading } = useSocietyRoles(open ? form.society_id : '');
    const roleOptions = roles.map(roleToOption).filter((o) => o.value != null && o.label);

    useEffect(() => {
        if (!open) return;
        if (isEdit && employee) {
            setForm({
                society_id: employee.society?.society_id ?? employee.society_id ?? '',
                first_name: employee.first_name ?? '',
                last_name: employee.last_name ?? '',
                phone: employee.phone ?? '',
                employee_type_id: employee.employeeType?.employee_type_id ?? employee.employee_type_id ?? '',
                role_id: employee.role?.role_id ?? employee.role_id ?? '',
                email: employee.email ?? '',
                password: '',
                designation: employee.designation ?? '',
                department: employee.department ?? '',
                employment_type: employee.employment_type ?? '',
                joining_date: toDateInput(employee.joining_date),
                ...profileFromEmployee(employee),
            });
        } else {
            setForm(emptyForm);
        }
        setErrors({});
    }, [open, isEdit, employee]);

    const setField = (name, value) => {
        setForm((p) => ({ ...p, [name]: normalizeProfileInput(name, value) }));
        if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    };

    const validate = () => {
        const e = {};
        if (!isEdit && !form.society_id) e.society_id = 'Community is required';
        if (!form.first_name.trim()) e.first_name = 'First name is required';
        if (!String(form.phone).trim()) e.phone = 'Phone is required';
        if (!form.employee_type_id) e.employee_type_id = 'Employee type is required';
        if (!form.joining_date) e.joining_date = 'Joining date is required';
        if (!form.email.trim()) e.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email';
        if (!isEdit) {
            if (!form.password) e.password = 'Password is required';
            else if (form.password.length < 6) e.password = 'Minimum 6 characters';
        }
        Object.assign(e, validateProfile(form));
        return e;
    };

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }

        const payload = {
            first_name: form.first_name.trim(),
            last_name: form.last_name.trim() || undefined,
            phone: String(form.phone).trim(),
            employee_type_id: form.employee_type_id,
            role_id: form.role_id || undefined,
            email: form.email.trim() || undefined,
            designation: form.designation.trim() || undefined,
            department: form.department || undefined,
            employment_type: form.employment_type || undefined,
            joining_date: form.joining_date || undefined,
            ...buildProfilePayload(form, { isEdit }),
        };
        if (!isEdit) {
            payload.society_id = form.society_id;
            if (form.password) payload.password = form.password;
        }
        if (isEdit) payload.id = employee.employee_id ?? employee.id;

        onSubmit(payload, { isEdit });
    };

    const profileText = (name, label, extra = {}) => (
        <TextField label={label} fullWidth size="small" sx={fieldSx}
            value={form[name]} onChange={(e) => setField(name, e.target.value)}
            error={!!errors[name]} helperText={errors[name]} {...extra} />
    );
    // SearchableDropdown has no label of its own — float one on the border so it
    // lines up with the outlined MUI fields around it.
    const profileSelect = (name, label, options) => (
        <Box sx={{ position: 'relative' }}>
            <SearchableDropdown
                value={form[name]}
                onChange={(v) => setField(name, v === 'all' ? '' : v)}
                options={options}
                placeholder={`Select ${label.toLowerCase()}`}
            />
            {form[name] && (
                <Typography sx={{ position: 'absolute', top: -8, left: 10, px: 0.5, bgcolor: '#fff', fontSize: 10, color: '#64748b', lineHeight: '16px', pointerEvents: 'none' }}>
                    {label}
                </Typography>
            )}
            {errors[name] && (
                <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>{errors[name]}</Typography>
            )}
        </Box>
    );

    const tone = isEdit ? '#059669' : '#487fff';
    const toneSoft = isEdit ? '#ecfdf5' : '#eef4ff';

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            TransitionComponent={Transition}
            // SearchableDropdown portals its search box to <body>; without this
            // the dialog's focus trap pulls focus back and the box can't be typed in.
            disableEnforceFocus
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
            {/* Header — clean, light, with a colored icon badge */}
            <Box sx={{ px: 3, py: 2.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eef1f6' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: toneSoft, color: tone, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon icon={isEdit ? 'solar:pen-new-square-bold-duotone' : 'solar:user-plus-bold-duotone'} style={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                            {isEdit ? 'Edit Employee' : 'Add Employee'}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                            {isEdit ? 'Update this employee record' : 'Create a new employee in a community'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} disabled={submitting} size="small"
                    sx={{ color: '#64748b', bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ px: 3, py: 2.5, bgcolor: '#fff' }}>
                {/* Basic information */}
                <Section icon="solar:user-rounded-linear" title="Basic Information">
                    <FieldGrid>
                        <TextField select label="Community" fullWidth size="small" sx={fieldSx}
                            value={form.society_id}
                            onChange={(e) => setForm((p) => ({ ...p, society_id: e.target.value, role_id: '' }))}
                            error={!!errors.society_id}
                            helperText={errors.society_id || (isEdit ? 'Use Reassign to move communities' : '')}
                            disabled={isEdit} required={!isEdit}>
                            <MenuItem value=""><em>Select community</em></MenuItem>
                            {societyOptions.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                        </TextField>
                        <TextField select label="Employee Type" fullWidth size="small" required sx={fieldSx}
                            value={form.employee_type_id}
                            onChange={(e) => setField('employee_type_id', e.target.value)}
                            error={!!errors.employee_type_id} helperText={errors.employee_type_id}>
                            <MenuItem value=""><em>Select type</em></MenuItem>
                            {typeOptions.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                        </TextField>
                        <TextField select label="Access Role" fullWidth size="small" sx={fieldSx}
                            value={form.role_id}
                            onChange={(e) => setField('role_id', e.target.value)}
                            disabled={!form.society_id || rolesLoading}
                            helperText={rolesLoading ? 'Loading roles…' : 'Optional — defaults from employee type'}>
                            <MenuItem value=""><em>{form.society_id ? 'Default (from type)' : 'Select community first'}</em></MenuItem>
                            {roleOptions.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                        </TextField>
                        <TextField label="First Name" fullWidth size="small" required sx={fieldSx}
                            value={form.first_name} onChange={(e) => setField('first_name', e.target.value)}
                            error={!!errors.first_name} helperText={errors.first_name} />
                        <TextField label="Last Name" fullWidth size="small" sx={fieldSx}
                            value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} />
                        <TextField label="Phone" fullWidth size="small" required sx={fieldSx}
                            value={form.phone} onChange={(e) => setField('phone', e.target.value)}
                            error={!!errors.phone} helperText={errors.phone} />
                    </FieldGrid>
                </Section>

                {/* Employment details */}
                <Section icon="solar:case-round-minimalistic-linear" title="Employment Details">
                    <FieldGrid>
                        <TextField label="Designation" fullWidth size="small" sx={fieldSx}
                            value={form.designation} onChange={(e) => setField('designation', e.target.value)} />
                        <TextField select label="Department" fullWidth size="small" sx={fieldSx}
                            value={form.department} onChange={(e) => setField('department', e.target.value)}>
                            <MenuItem value=""><em>None</em></MenuItem>
                            {DEPARTMENT_OPTIONS.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                        </TextField>
                        <TextField select label="Employment Type" fullWidth size="small" sx={fieldSx}
                            value={form.employment_type} onChange={(e) => setField('employment_type', e.target.value)}>
                            <MenuItem value=""><em>None</em></MenuItem>
                            {EMPLOYMENT_TYPE_OPTIONS.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                        </TextField>
                        <TextField label="Joining Date" type="date" fullWidth size="small" sx={fieldSx} required
                            InputLabelProps={{ shrink: true }}
                            value={form.joining_date} onChange={(e) => setField('joining_date', e.target.value)}
                            error={!!errors.joining_date} helperText={errors.joining_date} />
                    </FieldGrid>
                </Section>

                {/* Personal information */}
                <Section icon="solar:user-id-linear" title="Personal Information">
                    <FieldGrid>
                        {profileText('date_of_birth', 'Date of Birth', { type: 'date', InputLabelProps: { shrink: true } })}
                        {profileSelect('marital_status', 'Marital Status', MARITAL_STATUS_OPTIONS)}
                        {profileSelect('blood_group', 'Blood Group', BLOOD_GROUP_OPTIONS)}
                        {profileText('nationality', 'Nationality', { placeholder: 'e.g. Indian' })}
                        {profileText('qualification', 'Qualification', { placeholder: 'e.g. B.Com' })}
                        {profileText('pan_number', 'PAN Number', { placeholder: 'ABCDE1234F' })}
                        {profileText('aadhar_number', 'Aadhaar Number', { placeholder: '12-digit number' })}
                    </FieldGrid>
                </Section>

                {/* Bank details */}
                <Section icon="solar:card-linear" title="Bank Details">
                    <FieldGrid>
                        {profileText('bank_account_holder_name', 'Account Holder Name')}
                        {profileText('bank_name', 'Bank Name', { placeholder: 'e.g. HDFC Bank' })}
                        {profileText('bank_account_number', 'Account Number', { placeholder: '6–30 digits' })}
                        {profileText(CONFIRM_ACCOUNT_KEY, 'Confirm Account Number')}
                        {profileText('bank_ifsc_code', 'IFSC Code', { placeholder: 'HDFC0001234' })}
                        {profileText('bank_branch_name', 'Branch Name')}
                        {profileSelect('bank_account_type', 'Account Type', BANK_ACCOUNT_TYPE_OPTIONS)}
                        {profileText('upi_id', 'UPI ID', { placeholder: 'name@bank' })}
                    </FieldGrid>
                    {isEdit && (
                        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75, color: '#94a3b8' }}>
                            <Icon icon="solar:info-circle-linear" style={{ fontSize: 14, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 11.5 }}>
                                Personal & bank details are saved to this community's record only — other communities keep their own copy.
                            </Typography>
                        </Box>
                    )}
                </Section>

                {/* Login account */}
                <Section icon="solar:key-minimalistic-2-linear" title="Login Account">
                    <FieldGrid>
                        <TextField label="Email" type="email" fullWidth size="small" required sx={fieldSx}
                            value={form.email} onChange={(e) => setField('email', e.target.value)}
                            error={!!errors.email} helperText={errors.email} />
                        {!isEdit && (
                            <TextField label="Password" type="password" fullWidth size="small" required sx={fieldSx}
                                value={form.password} onChange={(e) => setField('password', e.target.value)}
                                error={!!errors.password}
                                helperText={errors.password || 'Minimum 6 characters'} />
                        )}
                    </FieldGrid>
                    {isEdit && (
                        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75, color: '#94a3b8' }}>
                            <Icon icon="solar:info-circle-linear" style={{ fontSize: 14, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 11.5 }}>
                                Password changes are managed separately and can't be edited here.
                            </Typography>
                        </Box>
                    )}
                </Section>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1.25, borderTop: '1px solid #eef1f6', bgcolor: '#fbfcfe' }}>
                <Button onClick={onClose} disabled={submitting} variant="text"
                    sx={{ color: '#475569', fontWeight: 600, textTransform: 'none', px: 2, '&:hover': { bgcolor: '#f1f5f9' } }}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={submitting} variant="contained" disableElevation
                    startIcon={submitting
                        ? <CircularProgress size={15} sx={{ color: '#fff' }} />
                        : <Icon icon={isEdit ? 'solar:diskette-bold' : 'solar:add-circle-bold'} style={{ fontSize: 17 }} />}
                    sx={{ bgcolor: tone, textTransform: 'none', fontWeight: 600, px: 2.5, borderRadius: 2, '&:hover': { bgcolor: tone, filter: 'brightness(0.94)' } }}>
                    {submitting ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Employee')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeFormModal;
